import { Box, Button, Flex, Heading, HStack, Tag, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiArrowLeft } from "react-icons/fi";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { NegotiationRoundV2Dto, Private, SourcingEventV2Dto } from "../../../../../autogen/bff-api";
import { ProcessStepBar } from "../../../../../common/layout/v2/ProcessStepBar";
import { urls } from "../../../../../urls";
import { NegotiationRoundStatusTag } from "../NegotiationRoundStatusTag";
import { RoundDetailsStep } from "./RoundDetailsStep";
import { RoundDocumentsStep } from "./RoundDocumentsStep";
import { RoundInvitationsStep } from "./RoundInvitationsStep";
import { RoundReviewStep } from "./RoundReviewStep";

const ROUND_WIZARD_STEPS = ["details", "documents", "invitations", "review"] as const;
export type RoundWizardStep = (typeof ROUND_WIZARD_STEPS)[number];

interface Props {
  event: SourcingEventV2Dto;
  round: NegotiationRoundV2Dto;
  refetchRound: () => void;
}

export const RoundWizardForm = ({ event, round, refetchRound }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const rawStep = searchParams.get("step");
  const currentStep: RoundWizardStep = ROUND_WIZARD_STEPS.some((s) => s === rawStep)
    ? (rawStep as RoundWizardStep)
    : "details";

  const stepLabels: Record<RoundWizardStep, string> = {
    details: t("Details"),
    documents: t("Documents"),
    invitations: t("Invitations"),
    review: t("Review and publish"),
  };

  const navigateToStep = (step: RoundWizardStep) =>
    navigate(`${urls.v2.sourcingEvents.editNegotiationRound.go(event.id, round.id)}?step=${step}`);

  const goBackToEvent = () => navigate(`${urls.v2.sourcingEvents.view.go(event.id)}?tab=negotiation`);

  const navPropsFor = (step: RoundWizardStep) => {
    const index = ROUND_WIZARD_STEPS.indexOf(step);
    const previous = ROUND_WIZARD_STEPS[index - 1];
    const next = ROUND_WIZARD_STEPS[index + 1];
    return {
      previousStep: previous ? () => navigateToStep(previous) : undefined,
      nextStep: next ? () => navigateToStep(next) : undefined,
      nextStepLabel: next ? stepLabels[next] : undefined,
    };
  };

  const currentIndex = ROUND_WIZARD_STEPS.indexOf(currentStep);

  return (
    <Box width="full" maxWidth="full">
      <Flex justify="space-between" align="flex-start" gap="4" flexWrap="wrap">
        <Box>
          <Heading as="h2" size="md">
            {event.eventData.title}
          </Heading>
          <HStack pt="2" spacing="3">
            <Tag>
              {t("Negotiation round")} {round.roundNumber}
            </Tag>
            <NegotiationRoundStatusTag status={round.status} />
          </HStack>
        </Box>
        <Button variant="ghost" size="sm" leftIcon={<FiArrowLeft />} onClick={goBackToEvent}>
          {t("Back to the event")}
        </Button>
      </Flex>

      <Box display={{ base: "none", md: "block" }} py="8" overflowX="auto">
        <ProcessStepBar
          maxW="full"
          steps={ROUND_WIZARD_STEPS.map((key) => ({ key, label: stepLabels[key] }))}
          currentKey={currentStep}
          onStepClick={(key) => navigateToStep(key as RoundWizardStep)}
        />
      </Box>
      <Text display={{ base: "block", md: "none" }} py="4" fontSize="sm" fontWeight="semibold" color="smPrimary">
        {t("Step")} {currentIndex + 1}/{ROUND_WIZARD_STEPS.length} · {stepLabels[currentStep]}
      </Text>

      <Box maxWidth="full" overflowX="hidden">
        <Box display={currentStep === "details" ? undefined : "none"}>
          <RoundDetailsStep round={round} {...navPropsFor("details")} />
        </Box>
        <Box display={currentStep === "documents" ? undefined : "none"}>
          <RoundDocumentsStep event={event} round={round} refetchRound={refetchRound} {...navPropsFor("documents")} />
        </Box>
        <Box display={currentStep === "invitations" ? undefined : "none"}>
          <RoundInvitationsStep event={event} round={round} {...navPropsFor("invitations")} />
        </Box>
        <Box display={currentStep === "review" ? undefined : "none"}>
          <RoundReviewStep
            event={event}
            round={round}
            previousStep={navPropsFor("review").previousStep}
            onPublished={goBackToEvent}
          />
        </Box>
      </Box>
    </Box>
  );
};
